import _includes from 'lodash/includes';

/**
 * Localization Utilities
 *
 * Helps to solve problems when working with different countries and locales.
 * Makes it easier when validating, formatting, getting date values.
 *
 */

/**
 * The list of all supported localized countries and their settings.
 * showGuestTermsText: Used for showing terms in small letters(looks like subtext)
 * showGuestTermsCheckBox: Used for showing a checkbox and terms
 * showGuestTermsWithoutCheckBox: The content style looks like "showGuestTermsCheckBox" but the checkbox is not displayed
 */
const countryInfo = {
	US: {
		showDOB: false,
		showOccupation: false,
		showNationality: false,
		showLegalCheckBox: true,
		showLegalTextOnly: false,
		showPasswordHint: false,
		showConsentCheckBox: false,
		DOBPattern: 'MM/DD/YYYY',
		occupations: [],
		showGuestTermsText: true,
		showGuestTermsCheckBox: false,
		showPhoneCountryCode: false,
		showGuestCPF: true,
		showHurricaneHarveyPromotion: true,
		termsAndConditionsWithFeeInformationDoc: false,
	},
	CA: {
		showDOB: true,
		showOccupation: true,
		showNationality: false,
		showLegalCheckBox: false,
		showLegalTextOnly: true,
		showPasswordHint: true,
		showConsentCheckBox: true,
		consentCheckBoxEnabled: true,
		DOBPattern: 'MM/DD/YYYY',
		showGuestTermsText: true,
		showGuestTermsCheckBox: false,
		showPhoneCountryCode: false,
		showGuestCPF: false,
		showHurricaneHarveyPromotion: false,
		termsAndConditionsWithFeeInformationDoc: false,
		occupations: [
			{ label: 'Accounting' },
			{ label: 'Administration' },
			{ label: 'Arts, Culture' },
			{ label: 'Business' },
			{ label: 'Communications' },
			{ label: 'Customer Service' },
			{ label: 'Education' },
			{ label: 'Energy, Utilities' },
			{ label: 'Engineering' },
			{ label: 'Finance' },
			{ label: 'Financial Services' },
			{ label: 'Government' },
			{ label: 'Health' },
			{ label: 'Hospitality' },
			{ label: 'Human Resources' },
			{ label: 'Internet' },
			{ label: 'Legal' },
			{ label: 'Manufacturing' },
			{ label: 'Marketing' },
			{ label: 'Non-profit' },
			{ label: 'Recreation' },
			{ label: 'Religion' },
			{ label: 'Research' },
			{ label: 'Sales' },
			{ label: 'Sports, Fitness' },
			{ label: 'Student' },
			{ label: 'Technology' },
			{ label: 'Trade' },
			{ label: 'Transportation' },
			{ label: 'Travel' }
		]
	},
	GB: {
		showDOB: true,
		showDOBHelp: true,
		showDOBMust18: true,
		showOccupation: false,
		showNationality: true,
		showLegalCheckBox: true,
		showLegalTextOnly: false,
		showPasswordHint: true,
		showConsentCheckBox: false,
		consentCheckBoxEnabled: false,
		DOBPattern: 'DD/MM/YYYY',
		occupations: [],
		showGuestTermsText: false,
		showGuestTermsCheckBox: false,
		showGuestTermsWithoutCheckBox: true,
		showPhoneCountryCode: false,
		showGuestCPF: false,
		showHurricaneHarveyPromotion: false,
		termsAndConditionsWithFeeInformationDoc: false,
	},
	AU: {
		showDOB: true,
		showDOBHelp: true,
		showDOBMust18: true,
		showOccupation: false,
		showNationality: false,
		showLegalCheckBox: false,
		showLegalTextOnly: true,
		showPasswordHint: true,
		showConsentCheckBox: true,
		consentCheckBoxEnabled: false,
		DOBPattern: 'DD/MM/YYYY',
		occupations: [],
		showGuestTermsText: false,
		showGuestTermsCheckBox: true,
		showPhoneCountryCode: false,
		showGuestCPF: false,
		showHurricaneHarveyPromotion: false,
		termsAndConditionsWithFeeInformationDoc: false,
	},
	FR: {
		showDOB: true,
		showDOBHelp: true,
		showDOBMust18: true,
		showOccupation: false,
		showNationality: true,
		showLegalCheckBox: true,
		showLegalTextOnly: false,
		showPasswordHint: false,
		showConsentCheckBox: false,
		consentCheckBoxEnabled: false,
		DOBPattern: 'DD/MM/YYYY',
		occupations: [],
		showGuestTermsText: false,
		showGuestTermsCheckBox: false,
		showGuestTermsWithoutCheckBox: true,
		showPhoneCountryCode: false,
		showGuestCPF: false,
		showHurricaneHarveyPromotion: false,
		termsAndConditionsWithFeeInformationDoc: false,
	},
	IT: {
		showDOB: true,
		showDOBHelp: true,
		showDOBMust18: true,
		showOccupation: false,
		showNationality: false,
		showLegalCheckBox: true,
		showLegalTextOnly: false,
		showPasswordHint: false,
		showConsentCheckBox: false,
		consentCheckBoxEnabled: false,
		DOBPattern: 'DD/MM/YYYY',
		occupations: [],
		showGuestTermsText: false,
		showGuestTermsCheckBox: false,
		showGuestTermsWithoutCheckBox: true,
		showPhoneCountryCode: false,
		showGuestCPF: false,
		showHurricaneHarveyPromotion: false,
		termsAndConditionsWithFeeInformationDoc: false,
	},
	IL: {
		showDOB: true,
		showDOBHelp: true,
		showDOBMust18: true,
		showOccupation: false,
		showNationality: false,
		showLegalCheckBox: true,
		showLegalTextOnly: false,
		showPasswordHint: false,
		showConsentCheckBox: false,
		consentCheckBoxEnabled: false,
		DOBPattern: 'DD/MM/YYYY',
		occupations: [],
		showGuestTermsText: false,
		showGuestTermsCheckBox: true,
		showGuestCPF: false,
		showHurricaneHarveyPromotion: false,
		termsAndConditionsWithFeeInformationDoc: false,
	},
	ES: {
		showDOB: true,
		showDOBHelp: true,
		showDOBMust18: true,
		showOccupation: false,
		showNationality: false,
		showLegalCheckBox: true,
		showLegalTextOnly: false,
		showPasswordHint: false,
		showConsentCheckBox: false,
		consentCheckBoxEnabled: false,
		DOBPattern: 'DD/MM/YYYY',
		occupations: [],
		showGuestTermsText: false,
		showGuestTermsCheckBox: false,
		showGuestTermsWithoutCheckBox: true,
		showPhoneCountryCode: false,
		showGuestCPF: false,
		showHurricaneHarveyPromotion: false,
		termsAndConditionsWithFeeInformationDoc: true,
	},
	IE: {
		showDOB: true,
		showDOBHelp: true,
		showDOBMust18: true,
		showOccupation: false,
		showNationality: false,
		showLegalCheckBox: true,
		showLegalTextOnly: false,
		showPasswordHint: false,
		showConsentCheckBox: false,
		consentCheckBoxEnabled: false,
		DOBPattern: 'DD/MM/YYYY',
		occupations: [],
		showGuestTermsText: false,
		showGuestTermsCheckBox: false,
		showGuestTermsWithoutCheckBox: true,
		showPhoneCountryCode: false,
		showGuestCPF: false,
		showHurricaneHarveyPromotion: false,
		termsAndConditionsWithFeeInformationDoc: false,
	},
	NL: {
		showDOB: true,
		showDOBHelp: true,
		showDOBMust18: true,
		showOccupation: false,
		showNationality: false,
		showLegalCheckBox: true,
		showLegalTextOnly: false,
		showPasswordHint: false,
		showConsentCheckBox: false,
		consentCheckBoxEnabled: false,
		DOBPattern: 'DD/MM/YYYY',
		occupations: [],
		showGuestTermsText: false,
		showGuestTermsCheckBox: false,
		showGuestTermsWithoutCheckBox: true,
		showPhoneCountryCode: true,
		showGuestCPF: false,
		showHurricaneHarveyPromotion: false,
		termsAndConditionsWithFeeInformationDoc: false,
	},
	AT: {
		showDOB: true,
		showDOBMust18: true,
		showOccupation: false,
		showNationality: false,
		showLegalCheckBox: true,
		showLegalTextOnly: false,
		showPasswordHint: false,
		showConsentCheckBox: false,
		DOBPattern: 'DD.MM.YYYY',
		occupations: [],
		showGuestTermsText: false,
		showGuestTermsCheckBox: false,
		showGuestTermsWithoutCheckBox: true,
		showPhoneCountryCode: true,
		showGuestCPF: false,
		showHurricaneHarveyPromotion: false,
		termsAndConditionsWithFeeInformationDoc: false,
	},
	CH: {
		showDOB: true,
		showDOBHelp: true,
		showDOBMust18: true,
		showOccupation: false,
		showNationality: false,
		showLegalCheckBox: true,
		showLegalTextOnly: false,
		showPasswordHint: false,
		showConsentCheckBox: false,
		consentCheckBoxEnabled: false,
		DOBPattern: 'DD/MM/YYYY',
		occupations: [],
		showGuestTermsText: false,
		showGuestTermsCheckBox: true,
		showPhoneCountryCode: true,
		showGuestCPF: false,
		showHurricaneHarveyPromotion: false,
		termsAndConditionsWithFeeInformationDoc: false,
		showNationalId: true,
		nationalIds: [
			{ label: 'Identity card' },
			{ label: 'Passport' },
			{ label: 'Driving licence' },
		]
	}
};

// AMLD4 requirements
// https://rally1.rallydev.com/#/37239556130d/detail/userstory/253577875956
const countriesWithAgreeAndDonateLabel = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK',
	'EE', 'FI', 'FR', 'GF', 'DE', 'GI', 'GP', 'GR', 'HU', 'IS', 'IE', 'IT', 'LV',
	'LI', 'LT', 'LU', 'MT', 'MQ', 'YT', 'NO', 'PL', 'PT', 'RE', 'RO', 'SM', 'SK',
	'SI', 'ES', 'SE', 'NL', 'GB', 'VA'];

/**
 * Returns a valid settings for specific country.
 *
 * @param {string} country code.
 * @return {Object} country settings.
 */
export function getCountryInfo(country = 'US') {
	return countryInfo[country] ? countryInfo[country] : countryInfo.US;
}

/**
 * Returns a date of birth string depending on the country format.
 *
 * @param {string} date of birth
 * @param {string} country code
 * @return {string} birthDate
 */
export function getFormattedDob(dob, country = 'US') {
	let fitDate = '';
	let flatDate = dob.replace(/\D+/g, '').slice(0, 8);
	const pattern = getCountryInfo(country).DOBPattern;

	let j = 0;
	for (let i = 0; i < flatDate.length; i++) {
		if (pattern[j] === '/') {
			fitDate += '/';
			j++;
		}

		fitDate += flatDate[i];
		j++;
	}

	return fitDate;
}

/**
 * Validates date of birth from DoB string.
 *
 * @param {string} date of birth
 * @return {boolean} is valid
 */
export function validateDob(dob, country = 'US') {
	let isValid = (dob && dob.length === 10) || false;

	if (isValid) {
		const dobDate = dob.split('/');
		const patterns = getCountryInfo(country).DOBPattern.split('/');
		const year = dobDate[patterns.indexOf('YYYY')];
		const month = dobDate[patterns.indexOf('MM')] - 1;
		const day = dobDate[patterns.indexOf('DD')];

		const checkDate = new Date(year, month, day);
		isValid = month === checkDate.getMonth();
	}

	return isValid;
}

export function isAgreeAndDonateLabel(country) {
	return _includes(countriesWithAgreeAndDonateLabel, country);
}



// WEBPACK FOOTER //
// ./utility/localizer.js